"use client";

import { useRef, type ReactNode } from "react";
import { useLoginViewportHeight } from "@/hooks/use-login-viewport-height";
import {
  DS_LOGIN_MESH_DRIFT_CLASS,
  DS_LOGIN_SHELL_CLASS,
  DS_LOGIN_WING_AURORA_BASE_COLOR,
  DS_LOGIN_WING_AURORA_MESH_LAYERS,
  DS_LOGIN_WING_AURORA_MESH_LAYERS_MOBILE,
} from "@/lib/core/design-system";
import { cn } from "@/lib/core/utils";

interface LoginBackgroundProps {
  children: ReactNode;
  className?: string;
}

/**
 * Wing Aurora Light — immersive login backdrop using canonical Angels mesh tokens.
 * Content is centered by `my-auto` at every breakpoint; on overflow the margins collapse
 * and the shell scrolls, so the card stays reachable when the mobile keyboard is open.
 */
export function LoginBackground({ children, className }: LoginBackgroundProps) {
  const shellRef = useRef<HTMLDivElement>(null);
  useLoginViewportHeight(shellRef);

  return (
    <div ref={shellRef} className={cn(DS_LOGIN_SHELL_CLASS, className)}>
      <div
        aria-hidden
        data-testid="login-wing-aurora-base"
        className="pointer-events-none fixed inset-0 -z-20"
        style={{ backgroundColor: DS_LOGIN_WING_AURORA_BASE_COLOR }}
      />
      <div
        aria-hidden
        data-testid="login-wing-aurora-mesh-mobile"
        className={cn(
          "pointer-events-none fixed inset-[-8%] -z-10 sm:hidden",
          DS_LOGIN_MESH_DRIFT_CLASS
        )}
        style={{
          backgroundImage: DS_LOGIN_WING_AURORA_MESH_LAYERS_MOBILE.join(", "),
        }}
      />
      <div
        aria-hidden
        data-testid="login-wing-aurora-mesh"
        className={cn(
          "pointer-events-none fixed inset-[-12%] -z-10 hidden sm:block",
          DS_LOGIN_MESH_DRIFT_CLASS
        )}
        style={{
          backgroundImage: DS_LOGIN_WING_AURORA_MESH_LAYERS.join(", "),
        }}
      />
      <div className="relative z-10 my-auto flex w-full max-w-md shrink-0 flex-col items-center">
        {children}
      </div>
    </div>
  );
}
